import { FC } from 'react'
import { PageHeader, EmptyState, PermissionIcon } from '@lmc/ui-kit'
import { ResidentStatus, UserRole, CernerRole } from '@lmc/ts-common'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import styled from 'styled-components'
import { useRouter } from 'hooks/useRouter'
import useResident from 'hooks/useResident'
import CernerHIE from './CernerHIE'
import { useSelector } from 'react-redux'
import { User, Home, ResidentItem } from '@lmc/ts-common'
import { cernerConstants } from 'locale/cerner'

type Props = {}

type ValidationCheckProps = {
  resident: ResidentItem
  hasSharedCareRecordEnabled: boolean | undefined
  hasSharedCareRecordPermission: boolean | undefined
  hasCernerRolePermission: boolean | undefined
  children: React.ReactNode
}

const ValidationCheck: FC<ValidationCheckProps & { testId?: string }> = ({
  resident,
  hasSharedCareRecordEnabled,
  hasSharedCareRecordPermission,
  hasCernerRolePermission,
  children,
  testId,
}) => {
  const { t } = useTranslation(NAMESPACE.CERNER)

  // Check for Clinical Role
  if (!hasSharedCareRecordPermission) {
    return (
      <EmptyState
        title={t(cernerConstants.PERMISSION_REQUIRED)}
        icon={<PermissionIcon />}
        description={t(cernerConstants.PERMISSION_DESCRIPTION)}
        data-testid={
          testId
            ? `${testId}-shared-care-record-permission-error`
            : 'shared-care-record-permission-error'
        }
      />
    )
  }

  // Check for Cerner Role Permission
  if (!hasCernerRolePermission) {
    return (
      <EmptyState
        title={t(cernerConstants.PERMISSION_REQUIRED)}
        icon={<PermissionIcon />}
        description={t(cernerConstants.INSUFFICIENT_CERNER_ROLE_DESCRIPTION)}
        data-testid={
          testId
            ? `${testId}-shared-care-record-cerner-role-error`
            : 'shared-care-record-cerner-role-error'
        }
      />
    )
  }

  // Check for ODS Code
  if (!hasSharedCareRecordEnabled) {
    return (
      <EmptyState
        title={t(cernerConstants.SITE_CONFIGURATION_REQUIRED)}
        icon={<PermissionIcon />}
        description={t(cernerConstants.SITE_CONFIGURATION_DESCRIPTION)}
        data-testid={
          testId
            ? `${testId}-shared-care-record-site-config-error`
            : 'shared-care-record-site-config-error'
        }
      />
    )
  }

  // Check for Active Service User
  if (resident.status !== ResidentStatus.active) {
    return (
      <EmptyState
        title={t(cernerConstants.INACTIVE_SERVICE_USER)}
        icon={<PermissionIcon />}
        description={t(cernerConstants.INACTIVE_SERVICE_USER_DESCRIPTION)}
        data-testid={
          testId
            ? `${testId}-shared-care-record-inactive-user-error`
            : 'shared-care-record-inactive-user-error'
        }
      />
    )
  }

  // Check for NHS Number
  if (!resident.nhsNumber) {
    return (
      <EmptyState
        title={t(cernerConstants.NHS_NUMBER_REQUIRED)}
        icon={<PermissionIcon />}
        description={t(cernerConstants.NHS_NUMBER_DESCRIPTION)}
        data-testid={
          testId
            ? `${testId}-shared-care-record-nhs-number-error`
            : 'shared-care-record-nhs-number-error'
        }
      />
    )
  }

  return children
}

export const SharedCareRecordScreen: FC<Props & { testId?: string }> = ({
  testId,
}) => {
  const { t } = useTranslation(NAMESPACE.CERNER)
  const {
    params: { id },
  } = useRouter()

  const { resident } = useResident({ id })

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home,
  )

  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user,
  )

  const hasSharedCareRecordEnabled = !!home?.odsCode
  const hasSharedCareRecordPermission =
    user?.role === UserRole.seniorCarer || user?.role === UserRole.careHomeAdmin
  const hasCernerRolePermission =
    !!user?.cernerRole &&
    [
      CernerRole.SeniorCarer,
      CernerRole.Admin,
      CernerRole.RegisteredManager,
    ].includes(user?.cernerRole)

  if (!resident) {
    return (
      <div
        data-testid={
          testId
            ? `${testId}-shared-care-record-loading`
            : 'shared-care-record-loading'
        }
      >
        Loading...
      </div>
    )
  }

  return (
    <MainContainer
      data-testid={
        testId
          ? `${testId}-shared-care-record-container`
          : 'shared-care-record-container'
      }
    >
      <PageHeader
        sticky
        title={`${resident?.name.first} ${resident?.name.last}'s London Care Record`}
        data-testid={
          testId
            ? `${testId}-shared-care-record-header`
            : 'shared-care-record-header'
        }
      />
      <SCRContainer
        data-testid={
          testId
            ? `${testId}-shared-care-record-content`
            : 'shared-care-record-content'
        }
      >
        <ValidationCheck
          resident={resident}
          hasSharedCareRecordEnabled={hasSharedCareRecordEnabled}
          hasSharedCareRecordPermission={hasSharedCareRecordPermission}
          hasCernerRolePermission={hasCernerRolePermission}
          testId={testId}
        >
          <IframeWrapper
            data-testid={
              testId
                ? `${testId}-shared-care-record-iframe-wrapper`
                : 'shared-care-record-iframe-wrapper'
            }
          >
            <CernerHIE residentId={resident.id} />
          </IframeWrapper>
        </ValidationCheck>
      </SCRContainer>
    </MainContainer>
  )
}

const MainContainer = styled.div`
  max-width: 1080px;
  margin: 24px auto;
  min-width: 1024px; // NHSx requirement
  min-height: 768px; // NHSx requirement
`

const SCRContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  margin-bottom: 20px;
`

const IframeWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 80vh;
`
